import axios from "axios";
import axiosInstance from "./axiosInstance";
import { getAuthHeader } from "./ocrService";

export interface NormalizedPhotoImage {
  format: "png" | "jpeg" | "webp";
  base64: string;
}

export interface NormalizedPhoto {
  image: NormalizedPhotoImage;
  file: File;
}

export const photoErrorMessage = (value: unknown): string | undefined => {
  if (typeof value === "string" && value.trim()) return value;
  if (!value || typeof value !== "object") return undefined;
  for (const key of ["message", "detail", "error"] as const) {
    if (key in value) {
      const message = photoErrorMessage(
        (value as Record<string, unknown>)[key],
      );
      if (message) return message;
    }
  }
  return undefined;
};

export const NormalizePhotoApi = async (
  file: File,
  signal?: AbortSignal,
): Promise<NormalizedPhoto> => {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axiosInstance.post(
      "/ocr/photo/normalize",
      formData,
      {
        headers: { ...getAuthHeader(), "Content-Type": "multipart/form-data" },
        signal,
      },
    );
    const result = response.data;
    if (result?.success !== true) {
      throw new Error(
        photoErrorMessage(result) || "Photo normalization failed.",
      );
    }
    const image = result.data?.image;
    if (
      !image ||
      !["png", "jpeg", "webp"].includes(image.format) ||
      typeof image.base64 !== "string" ||
      !image.base64.trim()
    ) {
      throw new Error(
        "The photo service returned an invalid image. Please reupload the photo.",
      );
    }
    let buffer: ArrayBuffer;
    try {
      const decoded = atob(image.base64);
      buffer = new ArrayBuffer(decoded.length);
      const bytes = new Uint8Array(buffer);
      for (let index = 0; index < decoded.length; index++) {
        bytes[index] = decoded.charCodeAt(index);
      }
    } catch {
      throw new Error(
        "The photo service returned invalid image data. Please reupload the photo.",
      );
    }
    return {
      image: { format: image.format, base64: image.base64 },
      file: new File([buffer], `normalized-photo.${image.format}`, {
        type: `image/${image.format}`,
      }),
    };
  } catch (error) {
    if (axios.isCancel(error)) throw error;
    throw new Error(
      (axios.isAxiosError(error)
        ? photoErrorMessage(error.response?.data)
        : undefined) ||
        (error instanceof Error ? error.message : "Failed to normalize photo."),
    );
  }
};
